import * as React from 'react'
import { Dialog as AriaDialog, Modal, ModalOverlay } from 'react-aria-components'
import { cn } from '../../lib/utils'
import { CloseButton } from './close-button'
import { useOverlay, overlayBackdropClass } from './overlay'
import {
  DialogCloseContext,
  DialogDescriptionContext,
  useModalDescription,
} from './dialog'

/**
 * Sheet — a panel that slides in from a screen edge for filters, settings,
 * profile views, navigation, or any secondary task that shouldn't navigate away.
 * It's the edge-anchored sibling of `Dialog`: same composable header/body/footer
 * parts, same focus trap and `aria-describedby` wiring, but pinned to a `side` and
 * slid in rather than centered. State + trigger come from the `Overlay`
 * primitives, so a complete sheet is `OverlayWrapper` → `SheetOverlay` → `Sheet`:
 *
 *   <OverlayWrapper trigger={<Button>Filters</Button>}>
 *     <SheetOverlay side="right">
 *       <Sheet>
 *         <SheetHeader>
 *           <SheetTitle>Filters</SheetTitle>
 *           <SheetDescription>Narrow down the results.</SheetDescription>
 *         </SheetHeader>
 *         <SheetBody>…</SheetBody>
 *         <SheetFooter>
 *           <SheetClose appearance="outline">Cancel</SheetClose>
 *           <Button>Apply</Button>
 *         </SheetFooter>
 *       </Sheet>
 *     </SheetOverlay>
 *   </OverlayWrapper>
 *
 * `SheetHeader`, `SheetTitle`, `SheetDescription`, `SheetBody`, `SheetFooter`,
 * and `SheetClose` are the `Dialog` parts re-exported — the panels share one set
 * of slots so they stay visually and behaviorally identical. The slide/fade ride
 * react-aria's `data-[entering]` / `data-[exiting]` states (no animation
 * dependency, matching the rest of the design system). Dismiss via backdrop click,
 * the Escape key, or the top-right close button. (A future gesture-rich,
 * drag-to-close variant would be a separate `Drawer`.)
 */

export type SheetSide = 'top' | 'right' | 'bottom' | 'left'

/** Lets `Sheet` read the `side` set on its enclosing `SheetOverlay`. */
const SheetSideContext = React.createContext<SheetSide>('right')

/**
 * Per-side classes: how the overlay aligns the panel (`overlay`), the panel's
 * cross-axis size + the off-screen translate it animates from/to (`modal`), and
 * the panel's main-axis sizing + edge border/radius (`panel`). Vertical sides
 * fill the height and cap the width; horizontal sides fill the width, cap the
 * height (so a long `SheetBody` scrolls), and round the exposed inner edge.
 */
const sideConfig: Record<
  SheetSide,
  { overlay: string; modal: string; panel: string }
> = {
  right: {
    overlay: 'justify-end',
    modal:
      'h-full w-full max-w-md data-[entering]:translate-x-full data-[exiting]:translate-x-full',
    panel: 'h-full border-l border-fbui-gray-200',
  },
  left: {
    overlay: 'justify-start',
    modal:
      'h-full w-full max-w-md data-[entering]:-translate-x-full data-[exiting]:-translate-x-full',
    panel: 'h-full border-r border-fbui-gray-200',
  },
  top: {
    overlay: 'items-start',
    modal:
      'w-full data-[entering]:-translate-y-full data-[exiting]:-translate-y-full',
    panel: 'max-h-[85vh] rounded-b-xl border-b border-fbui-gray-200',
  },
  bottom: {
    overlay: 'items-end',
    modal:
      'w-full data-[entering]:translate-y-full data-[exiting]:translate-y-full',
    panel: 'max-h-[85vh] rounded-t-xl border-t border-fbui-gray-200',
  },
}

export interface SheetOverlayProps {
  /** Overlay panel — a `<Sheet>`. A function form receives `{ close }`. */
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode)
  /** Screen edge the sheet slides from. */
  side?: SheetSide
  /** Close on backdrop click (default true). */
  isDismissable?: boolean
  /** Disable closing via the Escape key (default false). */
  isKeyboardDismissDisabled?: boolean
  /** Classes for the sliding panel container (e.g. a wider `max-w-lg`). */
  className?: string
  /** Classes for the backdrop layer. */
  overlayClassName?: string
}

/**
 * The dimmed backdrop + edge-anchored, sliding panel container (react-aria's
 * `ModalOverlay` + `Modal`). Reads open state from the enclosing `OverlayWrapper`
 * and exposes `side` to the `Sheet` within.
 */
export const SheetOverlay = ({
  children,
  side = 'right',
  isDismissable = true,
  isKeyboardDismissDisabled = false,
  className,
  overlayClassName,
}: SheetOverlayProps) => {
  const { isOpen, setOpen, close } = useOverlay()
  const cfg = sideConfig[side]
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={setOpen}
      isDismissable={isDismissable}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      className={cn(overlayBackdropClass, cfg.overlay, overlayClassName)}
    >
      <Modal
        className={cn(
          'outline-none transition-transform duration-200 ease-out',
          cfg.modal,
          className
        )}
      >
        <SheetSideContext.Provider value={side}>
          {typeof children === 'function' ? children({ close }) : children}
        </SheetSideContext.Provider>
      </Modal>
    </ModalOverlay>
  )
}
SheetOverlay.displayName = 'SheetOverlay'

export interface SheetProps {
  /** Sheet content; a function form receives `{ close }`. */
  children: React.ReactNode | ((opts: { close: () => void }) => React.ReactNode)
  /** Show the default top-right close button. */
  showCloseButton?: boolean
  /** Classes for the sheet panel. */
  className?: string
  'aria-label'?: string
}

/**
 * The sheet panel. A flex column that fills its edge — vertical sides take the
 * full height, horizontal sides cap at `max-h-[85vh]` — so a `SheetBody` scrolls
 * while the header and footer stay put. Reads `side` from the enclosing
 * `SheetOverlay`. `children` may be a function receiving `{ close }`.
 */
export const Sheet = ({
  children,
  showCloseButton = true,
  className,
  ...props
}: SheetProps) => {
  const side = React.useContext(SheetSideContext)
  const { describedBy, descriptionCtx } = useModalDescription()
  return (
    <AriaDialog
      aria-describedby={describedBy}
      className={cn(
        'relative flex w-full flex-col overflow-hidden bg-fbui-base-white shadow-fbui-lg outline-none',
        sideConfig[side].panel,
        className
      )}
      {...props}
    >
      {({ close }) => (
        <DialogCloseContext.Provider value={close}>
          <DialogDescriptionContext.Provider value={descriptionCtx}>
            {typeof children === 'function' ? children({ close }) : children}
            {/* Rendered last (it's absolutely positioned) so react-aria's initial
                focus lands on the first meaningful control, not the ✕. */}
            {showCloseButton && (
              <CloseButton
                onClick={close}
                className="absolute right-4 top-4 z-10"
              />
            )}
          </DialogDescriptionContext.Provider>
        </DialogCloseContext.Provider>
      )}
    </AriaDialog>
  )
}
Sheet.displayName = 'Sheet'

// The sheet shares Dialog's title/description/header/body/footer/close slots —
// same markup, same context wiring — so they stay identical across both panels.
export {
  DialogHeader as SheetHeader,
  DialogTitle as SheetTitle,
  DialogDescription as SheetDescription,
  DialogBody as SheetBody,
  DialogFooter as SheetFooter,
  DialogClose as SheetClose,
  useDialogClose as useSheetClose,
} from './dialog'
